'use client';

import { Fragment, useEffect, useState } from 'react';
import { cn, tv } from '../_lib/utils';
import type { SanityImage } from '../types';
import { Anchor, type LinkShape } from '../Anchor';
import { Media } from '../Media';
import { Text } from '../Text';
import { Container } from '../Container';
import { Divider } from '../Divider';
import { IconResolver } from '../IconResolver';
import { ButtonLink } from '../Inputs/Button';
import ChevronDownIcon from '../_assets/icons/chevronDown.svg';

const styles = tv({
	slots: {
		card: 'relative bg-button-secondary-background rounded-lg transition-colors duration-fast',
	},
	variants: {
		size: {
			small: {
				card: 'p-5 lg:p-6',
			},
			medium: {
				card: 'p-5 lg:p-8',
			},
			large: {
				card: 'p-5 border border-button-secondary-border lg:py-12 lg:px-9',
			},
		},
	},
});

type FooterLogo = {
	darkImage?: SanityImage;
	lightImage?: SanityImage;
	link?: LinkShape;
	name: string;
};

type SocialProfileProps = {
	type: 'facebook' | 'linkedin' | 'twitter' | 'youtube';
	href: string;
};

export type FooterProps = {
	upNext?: LinkShape;
	copyright?: string;
	navigation?: LinkShape[];
	socialLinks?: SocialProfileProps[];
	socialLinksLabel?: string;
	logos?: FooterLogo[];
	logoLabel?: string;
	links?: LinkShape[];
	linkLabel?: string;
	locale: string;
	languages?: any[];
	languageLabel?: string;
	upNextText?: string;
};

export function Footer(props: FooterProps) {
	const languageLabel = props.languageLabel ?? 'Region:';
	const logoLabel = props.logoLabel ?? 'Certified by';
	const copyright = props.copyright ?? 'Copyright © 2025 VoCoVo All rights reserved.';
	const upNextText = props.upNextText ?? 'Up Next';

	const { card } = styles();

	const [pathname, setPathname] = useState('');
	const [languageOpen, setLanguageOpen] = useState(false);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setPathname(window.location.pathname);
		}
	}, []);

	return (
		<footer className='relative pb-16 z-50' data-component='Footer'>
			<Divider padding={64} />
			<Container className='grid gap-responsive-lg md:grid-cols-4'>
				<section
					aria-label='Up Next'
					className={card({
						size: 'large',
						className: cn('md:col-span-2', props.upNext ? 'hover:bg-button-secondary-background-hover' : ''),
					})}
				>
					{props.upNext && (
						<>
							<Text className='text-text-secondary mb-4' styleType='label-md'>
								{upNextText}
							</Text>

							<Text className='text-text-primary' styleType='heading-md'>
								<Anchor
									className="before:content-[''] before:bottom-0 before:left-0 before:right-0 before:top-0 before:absolute"
									href={props.upNext.href}
								>
									{props.upNext.label}
								</Anchor>
							</Text>
						</>
					)}
				</section>

				<div className='flex flex-col gap-responsive-lg md:col-span-2 lg:col-span-1'>
					<nav
						className={card({ size: 'small', className: 'flex-auto flex flex-col justify-between gap-6' })}
						aria-label='Primary footer links'
					>
						<Text className='text-text-tertiary' styleType='label-md'>
							{props.linkLabel}
						</Text>

						<ul className='flex flex-col gap-2.5'>
							{props.links?.map((item, i) => (
								<Text as='li' key={i} className='text-text-secondary' styleType='body-sm-medium'>
									<Anchor className='link' href={item.href}>
										{item.label}
									</Anchor>
								</Text>
							))}
						</ul>
					</nav>

					<nav className={card({ size: 'small', className: 'flex flex-col gap-6' })} aria-label='Social media links'>
						<Text className='text-text-tertiary' styleType='label-md'>
							{props.socialLinksLabel}
						</Text>

						{props.socialLinks && props.socialLinks.length > 0 && (
							<ul className={`flex flex-wrap gap-2.5`}>
								{props.socialLinks?.map((item, index) => {
									return (
										<li key={`${item.type}-${index}-footer-socials`}>
											<ButtonLink
												aria-label={`Follow Vocovo on ${item.type}`}
												parent='unused'
												href={item.href}
												iconOnly
												styleSize='large'
												styleType='tertiary'
											>
												<IconResolver className='w-3.5 h-3.5' icon={item.type === 'twitter' ? 'x-logo' : `${item.type}-logo`} />
											</ButtonLink>
										</li>
									);
								})}
							</ul>
						)}
					</nav>
				</div>

				<div className={card({ size: 'medium', className: 'flex flex-col md:col-span-full lg:col-span-1' })}>
					<section>
						<Anchor
							aria-label='Go to homepage'
							className='mb-8 inline-flex transition-opacity duration-normal speed-smooth hover:opacity-80'
							href='/'
						>
							<Logo />
						</Anchor>
					</section>

					{props.languages && props.languages.length > 0 && (
						<nav aria-label='Language selector' className='flex gap-2 mb-5'>
							<Text className='text-text-secondary' styleType='body-sm-semibold'>
								{languageLabel}
							</Text>

							<Text className={languageOpen ? 'open' : undefined} styleType='body-sm-semibold'>
								<button
									className={cn('text-text-brand flex items-center gap-0.5', languageOpen ? 'hidden' : '')}
									onClick={() => setLanguageOpen(!languageOpen)}
								>
									{props.locale ? props.languages.find(language => language.url === `/${props.locale}`).name : props.languages[0].name}{' '}
									<ChevronDownIcon className='h-3 w-3' />
								</button>

								<ul className={cn('flex flex-col gap-0.5', !languageOpen ? 'hidden' : '')}>
									{props.languages.map(({ name, url }, index) => (
										<Fragment key={`${name}-${index}-${url}-footer-languages`}>
											<li tabIndex={languageOpen ? 0 : -1}>
												<a href={`${url}/${pathname.split('/').slice(2).join('/')}`}>{name}</a>
											</li>
										</Fragment>
									))}
								</ul>
							</Text>
						</nav>
					)}

					{props.logos && props.logos.length > 0 && (
						<section className='mb-5 flex flex-col'>
							<Text as='span' className='text-text-secondary' styleType='body-2xs-medium'>
								{logoLabel}
							</Text>

							<ul className='flex flex-wrap gap-3'>
								{props.logos.map(({ darkImage, lightImage, link, name }, index) => (
									<li className='group relative' key={`${name}-${index}-footer-logos-${link?.href}`}>
										<Text
											as='span'
											className='text-text-secondary py-2 transition-colors duration-fast ease-smooth group-hover:text-text-primary'
											styleType='body-2xs-medium'
										>
											{name}
										</Text>
										<div className='absolute h-40 w-40 flex items-center justify-center bottom-[100%] left-1/2 -translate-x-1/2 pointer-events-none opacity-0 translate-y-2 transition-all duration-fast ease-smooth group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0'>
											{link?.href ? (
												<Anchor className='flex transition-opacity duration-normal speed-smooth hover:opacity-80' href={link.href}>
													<Media className='hidden w-full dark:block' image={lightImage} />
													<Media className='block w-full dark:hidden' image={darkImage} />
												</Anchor>
											) : (
												<>
													<Media className='hidden w-full dark:block' image={lightImage} />
													<Media className='block w-full dark:hidden' image={darkImage} />
												</>
											)}
										</div>
									</li>
								))}
							</ul>
						</section>
					)}

					<nav aria-label='Secondary footer links'>
						<ul className='flex flex-col gap-2 mt-auto'>
							{props.navigation?.map((item, i) => (
								<Text as='li' key={`${item.label}-${i}-footer-navigation`} className='text-text-secondary' styleType='body-2xs-medium'>
									<Anchor className='link' href={item.href}>
										{item.label}
									</Anchor>
								</Text>
							))}

							<Text as='li' className='text-text-secondary' styleType='body-2xs-medium'>
								{copyright}
							</Text>

							<Text as='li' className='text-text-secondary' styleType='body-2xs-medium'>
								<span>Site by</span>{' '}
								<Anchor className='link' href='https://www.evensix.com'>
									evensix
								</Anchor>
							</Text>
						</ul>
					</nav>
				</div>
			</Container>
		</footer>
	);
}

function Logo() {
	return (
		<svg width='122' height='34' viewBox='0 0 122 34' xmlns='http://www.w3.org/2000/svg'>
			<path
				d='M7.09365 26.9473L22.8315 9.08256C23.1506 8.71853 23.1113 8.16906 22.746 7.85311C22.6466 7.7684 22.5309 7.7043 22.4038 7.66996L18.6207 6.63741C18.2854 6.54583 17.927 6.65572 17.705 6.91901L8.49493 17.7986L5.65074 3.60615C5.58599 3.27876 5.33857 3.01776 5.01484 2.93076L1.11389 1.86615C0.646799 1.73794 0.161204 2.01039 0.0317123 2.47515C-0.00528539 2.60794 -0.0099101 2.74989 0.0178382 2.88497L4.94547 26.3909C5.08652 27.0549 5.74554 27.4784 6.41613 27.3388C6.67742 27.2838 6.91328 27.1488 7.09133 26.9496'
				fill='currentColor'
			/>
			<path
				d='M97.2105 1.01673L92.6621 14.4948L84.777 2.3263C84.5943 2.04699 84.2682 1.89131 83.9353 1.92794L79.9279 2.35836C79.4377 2.41101 79.0839 2.84601 79.1371 3.33138C79.1533 3.46875 79.1996 3.60154 79.2782 3.7183L92.5303 23.7786C92.9049 24.3464 93.6749 24.5044 94.2484 24.1335C94.475 23.987 94.6461 23.7718 94.7363 23.5199L102.781 1.16325C102.943 0.70994 102.705 0.210836 102.247 0.0505729C102.122 0.00707303 101.988 -0.00895324 101.858 0.00478357L97.962 0.421467C97.6152 0.458098 97.3238 0.691624 97.2128 1.01673'
				fill='currentColor'
			/>
			<path
				d='M33.0333 22.9681L33.0425 22.904C33.498 20.3535 31.7777 17.9175 29.2017 17.4665C29.1601 17.4596 29.1208 17.4528 29.0791 17.4459C26.5887 17.075 24.2694 18.7738 23.8948 21.2395C23.8763 21.354 23.8648 21.4708 23.8555 21.5875L23.8463 21.6516C23.3884 24.1975 25.0996 26.6312 27.6732 27.0846C27.7195 27.0937 27.7657 27.1006 27.812 27.1075C30.3001 27.4783 32.6193 25.7818 32.9939 23.3184C33.0124 23.2016 33.024 23.0849 33.0333 22.9681ZM18.9117 21.0472L18.9186 20.9831C19.8297 15.7998 24.8128 12.3312 30.048 13.2333C34.9941 14.085 38.4465 18.5609 37.9771 23.5084L37.9678 23.5748C37.0498 28.7582 32.0621 32.2221 26.8292 31.3132C21.8923 30.4569 18.4492 25.9925 18.9117 21.0518'
				fill='currentColor'
			/>
			<path
				d='M41.1616 23.8861L41.1478 23.8174C39.6239 17.0612 43.9226 10.3599 50.7464 8.85346C50.9221 8.81453 51.0955 8.78019 51.2713 8.74814C54.5317 7.92164 57.991 8.40014 60.8976 10.0829C61.3138 10.3256 61.4549 10.8545 61.219 11.2688L59.4639 14.3642C59.2258 14.7809 58.6962 14.932 58.2708 14.7053C56.4579 13.643 54.3166 13.2721 52.2448 13.6613C48.1426 14.4924 45.8812 18.4738 46.7321 22.62L46.7483 22.6887C47.6015 26.8326 51.1949 29.6991 55.3711 28.8542C57.5331 28.4284 59.4431 27.1829 60.6895 25.3834C60.9739 25.0194 61.4942 24.9347 61.8827 25.1911L64.5766 26.9746C64.9835 27.2448 65.0922 27.7896 64.8217 28.1949C60.8398 34.1567 52.7304 35.7936 46.709 31.8535C43.8902 30.0081 41.9039 27.1555 41.1639 23.8907'
				fill='currentColor'
			/>
			<path
				d='M77.1434 15.1999L77.1226 15.1358C76.3965 12.6563 73.7789 11.23 71.2723 11.9489C71.2284 11.9626 71.1821 11.9763 71.1382 11.9901C68.7426 12.741 67.4153 15.2709 68.1738 17.6428C68.2108 17.7549 68.2501 17.8671 68.2963 17.977L68.3171 18.0411C69.0409 20.5206 71.6608 21.9515 74.1651 21.2326C74.2113 21.2189 74.2553 21.2052 74.3015 21.1914C76.6971 20.4405 78.0244 17.9106 77.266 15.5387C77.229 15.4243 77.1896 15.3144 77.1434 15.2022M63.6184 19.6552L63.5976 19.5934C62.1108 14.5428 65.0428 9.25644 70.1439 7.78431C74.9536 6.39689 80.0315 8.8947 81.8213 13.5263L81.8421 13.5858C83.3243 18.6387 80.3899 23.9228 75.2889 25.3903C70.4838 26.7732 65.4128 24.2776 63.6207 19.6529'
				fill='currentColor'
			/>
			<path
				d='M116.875 13.7163L116.896 13.6545C117.8 11.2231 116.54 8.52608 114.084 7.63089C114.045 7.61716 114.003 7.60113 113.962 7.58739C111.582 6.78608 108.999 8.04529 108.19 10.3989C108.151 10.5133 108.116 10.6278 108.086 10.7446L108.065 10.8064C107.161 13.2378 108.419 15.9348 110.875 16.83C110.916 16.846 110.956 16.8597 110.997 16.8735C113.374 17.6748 115.96 16.4156 116.769 14.0597C116.808 13.9452 116.843 13.8285 116.873 13.7117M103.318 9.348L103.339 9.28618C105.166 4.34551 110.692 1.80649 115.685 3.61517C120.395 5.32311 122.989 10.3256 121.648 15.1106L121.627 15.1724C119.803 20.1131 114.276 22.6544 109.284 20.848C104.571 19.1401 101.975 14.1353 103.318 9.348Z'
				fill='currentColor'
			/>
		</svg>
	);
}
